/**
 * Analytics API Service
 * Fleet Analytics page: pickup trends, service time, wait-at-merchant.
 */

import api from './api';

export interface TrendDay {
  day: string;
  created: number;
  collected: number;
  cancelled: number;
  still_active: number;
  orders_total: number;
  /** From the status pivot — survives soft-deletes, unlike `cancelled`. */
  cancelled_events: number;
}

export interface PickupTrendData {
  window_days: number;
  days: TrendDay[];
}

export interface ServiceBucket {
  bucket: string;
  legs: number;
  service_p50_min: number;
  service_avg_min: number;
  per_package_p50_s: number;
}

export type MerchantClassification = 'wait_heavy' | 'bulk' | 'fast' | 'normal';

export interface MerchantServiceRow {
  merchant_id: number;
  merchant_name: string;
  legs: number;
  median_orders: number;
  service_p50_min: number;
  per_package_p50_s: number;
  classification: MerchantClassification;
}

export interface DriverPaceRow {
  driver_id: number;
  legs: number;
  per_package_p50_s: number;
}

export interface ServiceTimeReport {
  window_days: number;
  legs: number;
  orders: number;
  service_min: { avg: number; p50: number; p90: number; measured_p50: number };
  per_package_s: { p50: number; avg: number };
  batch_tapped_pct: number;
  in_band_pct: number;
  over_band_pct: number;
  buckets: ServiceBucket[];
  merchants: MerchantServiceRow[];
  drivers: DriverPaceRow[];
}

export interface MerchantWaitReport {
  window_days: number;
  visits: number;
  measurable: number;
  measurable_pct: number;
  wait_p50_min: number;
  wait_p90_min: number;
  over_5min: number;
}

export interface EstimateVsActualReport {
  window_days: number;
  coverage: { stops_planned: number; stops_matched: number; match_rate_pct: number };
  arrival: {
    bias_min: number;
    abs_p50_min: number;
    abs_p90_min: number;
    within_15_pct: number;
    within_30_pct: number;
    within_60_pct: number;
  };
  verso_arrival: { abs_p50_min: number; abs_p90_min: number } | null;
  service: { planned_avg_min: number; actual_avg_min: number; err_p50_min: number; err_p90_min: number };
  task_finish: { abs_p50_min: number; within_30_pct: number };
  route_finish: { routes: number; err_p50_min: number; overrun_30_pct: number };
  by_day: Array<{ day: string; stops: number; abs_p50_min: number; within_30_pct: number }>;
  by_driver: Array<{ driver_uid: string; driver_name: string; stops: number; abs_p50_min: number; bias_min: number }>;
  worst_stops: Array<{
    task_uid: string;
    driver_name: string;
    planned_eta: string;
    actual_arrived: string;
    err_min: number;
  }>;
}

export interface MerchantPrediction {
  merchant_id: number;
  merchant_name: string;
  legs: number;
  pickups_per_week: number;
  expected_orders: number;
  avg_orders: number;
  learned_pace_s_per_order: number | null;
  learned_base_min: number | null;
  predicted_duration_min: number;
  slow_prep: boolean;
}

export interface MerchantPredictionsReport {
  window_days: number;
  merchants: MerchantPrediction[];
  model_fallback: { base_min: number; sec_per_order: number };
}

export interface SmallVisitMerchantRow {
  merchant_id: number;
  merchant_name: string;
  pickups: number;
  orders: number;
  avg_orders: number;
  empty_pickups: number;
  pickups_share_pct: number;
}

/** Echoed on every analytics report so the UI can label exactly what it shows. */
export interface AnalyticsWindowMeta {
  from_date: string;
  to_date: string;
  days: number;
  includes_today: boolean;
}

export interface SmallVisitMerchantsReport {
  window_days: number;
  window: AnalyticsWindowMeta;
  rule: { min_pickups: number; max_avg_orders: number };
  totals: { merchants: number; pickups: number; orders: number };
  flagged: { merchants: number; pickups: number; orders: number; pickups_pct: number; orders_pct: number };
  merchants: SmallVisitMerchantRow[];
}

export async function getPickupTrend(days = 7): Promise<PickupTrendData> {
  const response = await api.get<{ success: boolean; data: PickupTrendData }>(
    `/analytics/pickups-trend?days=${days}`
  );
  return response.data.data;
}

export async function getServiceTime(days = 7): Promise<ServiceTimeReport> {
  const response = await api.get<{ success: boolean; data: ServiceTimeReport }>(
    `/analytics/service-time?days=${days}`
  );
  return response.data.data;
}

export async function getMerchantWaits(days = 7): Promise<MerchantWaitReport> {
  const response = await api.get<{ success: boolean; data: MerchantWaitReport }>(
    `/analytics/wait-at-merchant?days=${days}`
  );
  return response.data.data;
}

export async function getEstimateVsActual(days = 7): Promise<EstimateVsActualReport> {
  const response = await api.get<{ success: boolean; data: EstimateVsActualReport }>(
    `/analytics/estimate-vs-actual?days=${days}`
  );
  return response.data.data;
}

export async function getMerchantPredictions(days = 30): Promise<MerchantPredictionsReport> {
  const response = await api.get<{ success: boolean; data: MerchantPredictionsReport }>(
    `/analytics/merchant-predictions?days=${days}`
  );
  return response.data.data;
}

export async function getSmallVisitMerchants(days = 30): Promise<SmallVisitMerchantsReport> {
  const response = await api.get<{ success: boolean; data: SmallVisitMerchantsReport }>(
    `/analytics/small-visit-merchants?days=${days}`
  );
  return response.data.data;
}
